import classnames from 'classnames'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import Button from '../../components/basics/Button/Button'
import TextInput from '../../components/basics/TextInput/TextInput'
import GroupAvatar from '../../components/GroupAvatar/GroupAvatar'
import OverlayChildPage from '../../components/OverlayChildPage/OverlayChildPage'
import Pagination from '../../components/Pagination/Pagination'
import UserSlugs from '../../components/slugs/UserSlugs'
import { userCanCreateSomeGroup } from '../../state/calculations/permissions/userCan'
import useGroupFilter from '../../state/queries/useGroupFilter'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'

import styles from './ManageGroupsPage.module.scss'

export default React.memo(function ManageGroupsPage() {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { user: currentUser } = useSession()

  const groupFilter = useGroupFilter()

  // search
  const updateSearch = useDebouncedCallback(groupFilter.setSearch, 300)
  const [search, setSearch] = useState(groupFilter.search)
  const handleSearchChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value)
      updateSearch(e.currentTarget.value)
    },
    [setSearch, updateSearch]
  )

  return (
    <article className={styles.ManageGroupsPage}>
      <header>
        <h1>{t('manage group.title manage groups')}</h1>
      </header>

      <main>
        <div className={styles.searchbar}>
          <TextInput
            name="search"
            label={t('search groups')}
            type="text"
            autoComplete="off"
            omitErrorDisplay
            value={search || ''}
            onChange={handleSearchChanged}
          />
          {userCanCreateSomeGroup(currentUser) && (
            <Button small filled component={Link} to={routes.createGroup.url({})}>
              {t('manage group.create group')}
            </Button>
          )}
        </div>

        <div className={classnames(styles.grouptable, { skeleton: groupFilter.result.isPlaceholderData })}>
          <table>
            <thead>
              <tr>
                <th className={styles.avatarColumn}></th>
                <th className={styles.nameColumn}>{t('name')}</th>
                <th className={styles.adminColumn}>{t('group.group managers')}</th>
                <th className={styles.infoColumn}>{t('group.number of members')}</th>
                <th className={styles.infoColumn}>{t('group.created at')}</th>
              </tr>
            </thead>
            <tbody>
              {groupFilter.groups.map((group) => (
                <tr key={group.uuid}>
                  <td>
                    <GroupAvatar group={group} />
                  </td>
                  <td className={styles.nameColumn}>
                    <Link to={routes.manageGroup.url({ uuid: group.uuid })}>{group.name}</Link>
                  </td>
                  <td className={styles.adminColumn}>
                    <UserSlugs uuids={group.managers?.map(({ uuid }) => uuid)} max={8} />
                  </td>
                  <td className={styles.membersColumn}>
                    <span>{t('group.group_number_of_members', { count: group.members.length })}</span>
                  </td>
                  <td className={styles.createdAtColumn}>
                    <span>{t('group.group_createdAt_date', { date: group.createdAt })}</span>
                  </td>
                </tr>
              ))}
              {!groupFilter.groups.length && (
                <tr>
                  <td colSpan={4} className={styles.empty}>
                    {t('no groups found')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <Pagination
            page={groupFilter.page}
            pageCount={groupFilter.pageCount}
            pageSize={groupFilter.pageSize}
            itemCount={groupFilter.itemCount}
            onPageChanged={groupFilter.setPage}
          />
        </div>
      </main>

      <OverlayChildPage />
    </article>
  )
})

export function ManageGroupsPageBreadcrumb() {
  const { t } = useTranslation()
  const routes = useRoutes()
  return <Link to={routes.manageGroups.url({})}>{t('manage group.title manage groups')}</Link>
}
